import React, { useCallback, useEffect, useState } from 'react';
import { Check, KeyRound, Loader, Lock, Trash2, X } from 'lucide-react';
import { Row, Section, Segmented, Toggle } from '../settingsPrimitives';
import { usePreferencesStore } from '../../store/preferencesStore';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import {
  AI_PROVIDERS,
  AI_SETTINGS_CHANGED_EVENT,
  getAiSettings,
  setAiSettings,
  providerMeta,
  markApibooDefaultMigrated,
} from '../../services/ai/aiSettings';
import {
  hasAiKey,
  setAiKey,
  removeAiKey,
  vaultKeyState,
  AiKeyVaultError,
} from '../../services/ai/keyStore';
import { testAiKey } from '../../services/ai/engine';
import {
  openVaultGate,
  VAULT_GATE_UNLOCKED_EVENT,
  VAULT_GATE_DISMISSED_EVENT,
  VAULT_UNLOCKED_UI_EVENT,
} from '../../store/vaultStore';
import { openExternal } from '../../utils/openExternal';
import AiApibooSection from './AiApibooSection';

/**
 * Settings → AI Chat (BYO key, 16.08.2026).
 *
 * Provider + model choice are plain preferences (services/ai/aiSettings.js);
 * the API key itself goes ONLY into the encrypted vault
 * (services/ai/keyStore.js) — there is deliberately no localStorage
 * fallback, and this page handles all three vault states:
 *
 *   unlocked  — save/read/remove keys directly.
 *   locked    — every key action routes through the existing passphrase
 *               gate (openVaultGate); the gate itself decides between the
 *               unlock modal and the first-time setup ceremony, so "locked"
 *               and "no vault yet" need only one button here.
 *   unavailable — not running under Tauri; explain instead of failing.
 *
 * The key is WRITE-ONLY in this UI: it is never rendered back, only a
 * "saved" state — replace or remove are the only operations on a stored key.
 */
/** Sentinel <option> value — cannot collide with a real model id. */
const CUSTOM_MODEL = '__custom__';

function AiChatSettings({ t }) {
  const aiReadResponses = usePreferencesStore((s) => s.aiReadResponses === true);
  const updatePreference = usePreferencesStore((s) => s.updatePreference);
  const [settings, setSettings] = useState(getAiSettings);
  const [vaultState, setVaultState] = useState(null); // null=checking
  const [keySaved, setKeySaved] = useState(null); // null=unknown
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind: 'ok'|'error', text }

  const meta = providerMeta(settings.provider);
  // A stored model the provider's list doesn't offer IS a custom one — that's
  // how the field survives a reopen (and how a hand-typed id stays visible
  // instead of silently reading back as the first entry in the dropdown).
  const [customModel, setCustomModel] = useState(() => !meta.models.includes(settings.model));
  const isCustomModel = customModel || !meta.models.includes(settings.model);

  // provider id -> bool, for EVERY provider (not just the active one): without
  // it, "do I already have an OpenAI key?" costs a provider switch to answer,
  // and switching clears the key field mid-edit.
  const [keyedByProvider, setKeyedByProvider] = useState({});

  const refresh = useCallback(async () => {
    const vs = await vaultKeyState();
    setVaultState(vs);
    if (vs !== 'unlocked') {
      setKeySaved(null);
      setKeyedByProvider({});
      return;
    }
    try {
      setKeySaved(await hasAiKey(getAiSettings().provider));
    } catch {
      setKeySaved(null);
    }
    const entries = await Promise.all(
      AI_PROVIDERS.map(async (p) => {
        try {
          return [p.id, await hasAiKey(p.id)];
        } catch {
          return [p.id, false];
        }
      })
    );
    setKeyedByProvider(Object.fromEntries(entries));
  }, []);

  useEffect(() => {
    refresh();
    // The vault can unlock from anywhere (the gate modal, the vault panel);
    // both funnels re-check so the "saved" state appears without a reopen.
    window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, refresh);
    window.addEventListener(VAULT_UNLOCKED_UI_EVENT, refresh);
    return () => {
      window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, refresh);
      window.removeEventListener(VAULT_UNLOCKED_UI_EVENT, refresh);
    };
  }, [refresh]);

  // The preference can change while this page is open — the panel's model
  // picker, or the one-time default migration that the AI panel's re-resolve
  // runs after a vault unlock (services/ai/apibooDefaultMigration.js). Re-seed
  // from storage like AiModelPicker does, so the page can never show a
  // provider storage does not hold (review B1, 22.08.2026).
  useEffect(() => {
    const sync = () => setSettings(getAiSettings());
    window.addEventListener(AI_SETTINGS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AI_SETTINGS_CHANGED_EVENT, sync);
  }, []);

  const applySettings = useCallback((patch) => {
    // An explicit provider pick IS "the user changed it": the default
    // migration must never flip it later, even if no key follows.
    if (patch.provider) markApibooDefaultMigrated();
    const next = setAiSettings(patch);
    setSettings(next);
    setFeedback(null);
    if (patch.provider) {
      setKeyInput('');
      // The new provider brings its own list; start on it, not in custom mode.
      setCustomModel(false);
      refresh();
    }
  }, [refresh]);

  /** Run a vault-touching action; on a locked vault open the gate and retry after unlock. */
  const withVault = useCallback(async (action) => {
    try {
      return await action();
    } catch (e) {
      if (e instanceof AiKeyVaultError && e.code === 'vault_locked') {
        const unlocked = await new Promise((resolve) => {
          const done = (ok) => {
            window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
            window.removeEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
            resolve(ok);
          };
          const onOk = () => done(true);
          const onNo = () => done(false);
          window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
          window.addEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
          openVaultGate('ai-chat');
        });
        if (!unlocked) throw e;
        return await action();
      }
      throw e;
    }
  }, []);

  const handleSaveKey = useCallback(async () => {
    const key = keyInput.trim();
    if (!key || busy) return;
    setBusy(true);
    setFeedback(null);
    // Saving a key for the provider on screen is a deliberate choice; mark it
    // BEFORE the vault round-trip — the gate can open inside withVault, and
    // the unlock re-resolve would otherwise migrate the stored provider
    // underneath this page (review B1).
    markApibooDefaultMigrated();
    try {
      await withVault(() => setAiKey(settings.provider, key));
      setKeyInput('');
      setFeedback({ kind: 'ok', text: t('settings.aiChatKeySaved') });
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', text: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }, [keyInput, busy, settings.provider, withVault, refresh, t]);

  const handleRemoveKey = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      await withVault(() => removeAiKey(settings.provider));
      setFeedback({ kind: 'ok', text: t('settings.aiChatKeyRemoved') });
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', text: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }, [busy, settings.provider, withVault, refresh, t]);

  const handleTestKey = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      // Test the freshly-typed key when there is one; the stored key otherwise.
      const typed = keyInput.trim();
      await withVault(() =>
        testAiKey({
          provider: settings.provider,
          model: settings.model,
          apiKey: typed || undefined,
        })
      );
      setFeedback({ kind: 'ok', text: t('settings.aiChatKeyTestOk') });
    } catch (e) {
      setFeedback({ kind: 'error', text: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }, [busy, keyInput, settings.provider, settings.model, withVault, t]);

  return (
    <div className="space-y-6">
      <Section title={t('settings.aiChatProviderSection')}>
        <Row
          label={t('settings.aiChatProvider')}
          // Keyless Apiboo must not claim "your own API key" (card d9789e8a).
          desc={t(meta.keyless ? 'settings.aiChatProviderDescApiboo' : 'settings.aiChatProviderDesc')}
        >
          {/* A dot on every provider that already has a key in the vault, so
              the answer to "which of these am I set up for?" is visible
              without switching provider (which clears the key field). */}
          <Segmented
            value={settings.provider}
            onChange={(id) => applySettings({ provider: id })}
            options={AI_PROVIDERS.map((p) => ({
              id: p.id,
              label: (
                <span className="ai-provider-option">
                  {p.label}
                  {keyedByProvider[p.id] && (
                    <span
                      className="ai-provider-keyed-dot"
                      title={t('settings.aiChatKeySavedBadge')}
                      data-testid={`ai-provider-keyed-${p.id}`}
                    />
                  )}
                </span>
              ),
            }))}
          />
        </Row>
        {/* Keyless provider: the server picks the model (shown in the
            Apiboo section below), so there is nothing to choose here. */}
        {!meta.keyless && (
        <Row
          label={t('settings.aiChatModel')}
          desc={t('settings.aiChatModelDesc')}
        >
          {/* CUSTOM is not a nicety: providers retire model ids between our
              releases (Gemini closed the 2.5 line to new keys in Aug 2026),
              and a hardcoded list with no escape hatch turns that into a
              provider nobody can use until the next app update. */}
          {/* Explicit width: Row drops its child into a `flex-shrink-0` box, and
              CompactSelectPicker's root is `width:100%`, which has nothing to
              resolve against there. */}
          <div style={{ width: 220 }}>
            <CompactSelectPicker
              className="settings-model-picker"
              value={isCustomModel ? CUSTOM_MODEL : settings.model}
              onChange={(next) => {
                if (next === CUSTOM_MODEL) {
                  setCustomModel(true);
                  return;
                }
                setCustomModel(false);
                applySettings({ model: next });
              }}
              options={[
                ...meta.models.map((m) => ({ value: m, label: m })),
                { value: CUSTOM_MODEL, label: t('settings.aiChatModelCustom') },
              ]}
              menuMinWidth={220}
              aria-label={t('settings.aiChatModel')}
            />
          </div>
        </Row>
        )}
        {!meta.keyless && isCustomModel && (
          <div className="flex items-center gap-2 py-1">
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={settings.model}
              onChange={(e) => applySettings({ model: e.target.value })}
              placeholder={meta.models[0]}
              className="settings-text-input flex-1 min-w-0"
              aria-label={t('settings.aiChatModelCustom')}
            />
          </div>
        )}
      </Section>

      {/* The response-privacy gate (owner decision 13.09.2026): opt-in, default
          OFF. Same preference as the eye toggle in the AI panel header. */}
      <Section title={t('settings.aiPrivacySection', 'Privacy')}>
        <Row
          label={t('settings.aiReadResponses', 'Let AI read responses')}
          desc={t('settings.aiReadResponsesDesc', 'Off: the assistant sees status codes, never response bodies. On: it can read bodies to write tests or explain failures, with tokens and secrets redacted first.')}
        >
          <Toggle value={aiReadResponses} onChange={(v) => updatePreference('aiReadResponses', v)} />
        </Row>
      </Section>

      {meta.keyless ? (
        <AiApibooSection t={t} />
      ) : (
      <Section title={t('settings.aiChatKeySection')}>
        {vaultState === 'unavailable' && (
          <div className="settings-row-desc py-2">
            {t('settings.aiChatVaultUnavailable')}
          </div>
        )}
        {vaultState === 'locked' && (
          <Row
            label={t('settings.aiChatVaultLocked')}
            desc={t('settings.aiChatVaultLockedDesc')}
          >
            <button
              type="button"
              onClick={() => {
                // Unlocking from the key section of provider X means the user
                // is about to act on X: settle the default migration first so
                // the panel's post-unlock re-resolve cannot switch X away.
                markApibooDefaultMigrated();
                openVaultGate('ai-chat');
              }}
              className="settings-action-btn settings-action-btn--tinted"
            >
              <Lock size={13} />
              {t('settings.aiChatUnlockVault')}
            </button>
          </Row>
        )}
        {vaultState === 'unlocked' && (
          <>
            <Row
              label={t('settings.aiChatApiKey', { provider: meta.label })}
              desc={keySaved
                ? t('settings.aiChatKeySavedDesc')
                : t('settings.aiChatKeyDesc')}
            >
              {keySaved && !keyInput ? (
                <span className="ai-key-feedback ai-key-feedback--ok" data-testid="ai-key-saved-badge">
                  <Check size={13} />
                  {t('settings.aiChatKeySavedBadge')}
                </span>
              ) : null}
            </Row>
            <div className="flex items-center gap-2 py-1">
              <input
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={keyInput}
                onChange={(e) => { setKeyInput(e.target.value); setFeedback(null); }}
                placeholder={keySaved ? t('settings.aiChatReplaceKeyPlaceholder') : meta.keyHint}
                className="settings-text-input flex-1 min-w-0"
                aria-label={t('settings.aiChatApiKey', { provider: meta.label })}
              />
              <button
                type="button"
                disabled={!keyInput.trim() || busy}
                onClick={handleSaveKey}
                className="settings-action-btn settings-action-btn--tinted flex-shrink-0"
              >
                <KeyRound size={13} />
                {keySaved ? t('settings.aiChatReplaceKey') : t('settings.aiChatSaveKey')}
              </button>
            </div>
            <div className="flex items-center gap-2 py-1">
              <button
                type="button"
                disabled={busy || (!keySaved && !keyInput.trim())}
                onClick={handleTestKey}
                className="settings-action-btn flex-shrink-0"
              >
                {busy ? <Loader size={13} className="animate-spin" /> : <Check size={13} />}
                {t('settings.aiChatTestKey')}
              </button>
              {keySaved && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleRemoveKey}
                  className="settings-action-btn settings-action-btn--danger flex-shrink-0"
                >
                  <Trash2 size={13} />
                  {t('settings.aiChatRemoveKey')}
                </button>
              )}
              <button
                type="button"
                onClick={() => openExternal(meta.consoleUrl)}
                className="settings-link-btn ai-key-console-link"
              >
                {t('settings.aiChatGetKey', { provider: meta.label })}
              </button>
            </div>
            {feedback && (
              <div
                className={`ai-key-feedback py-1 ${
                  feedback.kind === 'ok'
                    ? 'ai-key-feedback--ok'
                    : 'ai-key-feedback--error'
                }`}
                role="status"
              >
                {feedback.kind === 'ok' ? <Check size={12} /> : <X size={12} />}
                <span>{feedback.text}</span>
              </div>
            )}
          </>
        )}
      </Section>
      )}
    </div>
  );
}

export default AiChatSettings;
